import { useCallback, useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import {
  GenericFullscreenDialog,
  DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
  ProgressIndicator
} from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCreatePartnershipWithInstallation } from '@/features/suppliers/hooks/partnerships'
import toast from 'react-hot-toast'
import { validateSupplierForm, SupplierFormData } from '../components/SupplierForm.schema'
import {
  InstallationFormData,
  validateInstallationForm
} from '../pages/supplier-details/components/InstallationForm.schema'
import SupplierDetailsStep from '../components/SupplierCreateSteps/SupplierDetailsStep.tsx'
import SupplierAddressDetailsStep from '../components/SupplierCreateSteps/SupplierAddressDetailsStep.tsx'
import SupplierContactDetailsStep from '../components/SupplierCreateSteps/SupplierContactDetailsStep.tsx'
import SupplierReviewStep from '../components/SupplierCreateSteps/SupplierReviewStep.tsx'
import InstallationDetailsStep from '../components/SupplierCreateSteps/InstallationDetailsStep.tsx'
import InstallationAddressStep from '../components/SupplierCreateSteps/InstallationAddressStep.tsx'
import InstallationReviewStep from '../components/SupplierCreateSteps/InstallationReviewStep.tsx'

export default function SupplierCreateDialog({
  isOpen,
  close,
  organizationId
}: {
  isOpen: boolean
  close: () => void
  organizationId: string
}) {
  const { t } = useI18n()
  const navigate = useNavigate()

  const {
    mutateAsync: createPartnershipWithInstallation,
    isPending: isPendingCreatePartnershipWithInstallation
  } = useCreatePartnershipWithInstallation(organizationId)

  const [currentStep, setCurrentStep] = useState(0)

  const [supplierFormData, setSupplierFormData] = useState<SupplierFormData>({
    isBusinessRegistrationNumberNotKnown: false,
    supplierName: '',
    businessRegistrationNumber: '',
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    poBox: '',
    subDivision: '',
    country: '',
    contactName: '',
    contactPhoneNumber: '',
    contactEmail: ''
  })

  const [installationFormData, setInstallationFormData] = useState<InstallationFormData>({
    name: '',
    goodsFamily: '',
    ...DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
    usePartnershipAddress: true,
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    poBox: '',
    country: '',
    unlocode: '',
    longitude: 0,
    latitude: 0
  })

  useEffect(() => {
    if (supplierFormData.country) {
      setInstallationFormData(prev => ({
        ...prev,
        country: supplierFormData.country,
        ...(prev.usePartnershipAddress
          ? {
              street: supplierFormData.street,
              streetNumber: supplierFormData.streetNumber,
              street2: supplierFormData.street2,
              postCode: supplierFormData.postCode,
              city: supplierFormData.city,
              poBox: supplierFormData.poBox
            }
          : {})
      }))
    }
  }, [
    supplierFormData.country,
    supplierFormData.street,
    supplierFormData.streetNumber,
    supplierFormData.street2,
    supplierFormData.postCode,
    supplierFormData.city,
    supplierFormData.poBox
  ])

  useEffect(() => {
    const scrollableContainer = document.getElementsByClassName('fullscreen-dialog-content')[0]
    if (scrollableContainer) {
      scrollableContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentStep])

  const resetForm = useCallback(() => {
    setCurrentStep(0)
    setSupplierFormData({
      isBusinessRegistrationNumberNotKnown: false,
      supplierName: '',
      businessRegistrationNumber: '',
      street: '',
      streetNumber: '',
      street2: '',
      postCode: '',
      city: '',
      poBox: '',
      subDivision: '',
      country: '',
      contactName: '',
      contactPhoneNumber: '',
      contactEmail: ''
    })
    setInstallationFormData({
      name: '',
      goodsFamily: '',
      ...DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
      usePartnershipAddress: true,
      street: '',
      streetNumber: '',
      street2: '',
      postCode: '',
      city: '',
      poBox: '',
      country: '',
      unlocode: '',
      longitude: 0,
      latitude: 0
    })
  }, [])

  const onClose = useCallback(() => {
    resetForm()
    close()
  }, [close, resetForm])

  const onCreate = useCallback(async () => {
    try {
      const supplierValidation = validateSupplierForm(supplierFormData)
      const installationValidation = validateInstallationForm(installationFormData)

      if (!supplierValidation.success || !installationValidation.success) {
        return
      }

      const result = await createPartnershipWithInstallation({
        partnership: {
          name: supplierFormData.supplierName,
          country: supplierFormData.country,
          isOffPlatform: true,
          operatorStreet: supplierFormData.street,
          operatorStreet2: supplierFormData.street2,
          operatorStreetNumber: supplierFormData.streetNumber,
          operatorSubDivision: supplierFormData.subDivision,
          operatorPostCode: supplierFormData.postCode,
          operatorPoBox: supplierFormData.poBox,
          operatorCity: supplierFormData.city,
          operatorBusinessRegistrationNumber: supplierFormData.businessRegistrationNumber,
          operatorContactName: supplierFormData.contactName,
          operatorContactPhoneNumber: supplierFormData.contactPhoneNumber,
          operatorContactEmail: supplierFormData.contactEmail
        },
        installation: {
          ...installationValidation.data
        }
      })

      toast.success(
        t(
          'suppliers:createSupplierDialog.notifications.supplierAndInstallationCreatedSuccessfully'
        ),
        {
          position: 'top-right'
        }
      )
      onClose()
      navigate(`/suppliers/${result.partnership.id}`)
    } catch (error: any) {
      toast.error(t('suppliers:createSupplierDialog.notifications.failedToCreateSupplier'), {
        position: 'top-right'
      })
      console.error(error)
    }
  }, [
    supplierFormData,
    installationFormData,
    createPartnershipWithInstallation,
    t,
    onClose,
    navigate
  ])

  const partnershipAddress = useMemo(
    () => ({
      operatorStreet: supplierFormData.street,
      operatorStreetNumber: supplierFormData.streetNumber,
      operatorStreet2: supplierFormData.street2,
      operatorPostCode: supplierFormData.postCode,
      operatorCity: supplierFormData.city,
      operatorPoBox: supplierFormData.poBox
    }),
    [supplierFormData]
  )

  return (
    <GenericFullscreenDialog isOpen={isOpen} close={onClose}>
      <div className="pt-16">
        <ProgressIndicator totalSteps={7} currentStep={currentStep} />
        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={`step-${currentStep}`}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                duration: 0.5
              }}
            >
              {currentStep === 0 && (
                <SupplierDetailsStep
                  data={supplierFormData}
                  onNextClick={payload => {
                    setSupplierFormData({
                      ...supplierFormData,
                      ...payload
                    })
                    setCurrentStep(1)
                  }}
                  onBackClick={onClose}
                />
              )}
              {currentStep === 1 && (
                <SupplierAddressDetailsStep
                  data={supplierFormData}
                  onNextClick={payload => {
                    setSupplierFormData({
                      ...supplierFormData,
                      ...payload
                    })
                    setCurrentStep(2)
                  }}
                  onBackClick={() => setCurrentStep(0)}
                />
              )}
              {currentStep === 2 && (
                <SupplierContactDetailsStep
                  data={supplierFormData}
                  onNextClick={payload => {
                    setSupplierFormData({
                      ...supplierFormData,
                      ...payload
                    })
                    setCurrentStep(3)
                  }}
                  onBackClick={() => setCurrentStep(1)}
                />
              )}
              {currentStep === 3 && (
                <SupplierReviewStep
                  data={supplierFormData}
                  onNextClick={() => setCurrentStep(4)}
                  onBackClick={() => setCurrentStep(2)}
                />
              )}
              {currentStep === 4 && (
                <InstallationDetailsStep
                  data={installationFormData}
                  onNextClick={payload => {
                    setInstallationFormData({
                      ...installationFormData,
                      ...payload
                    })
                    setCurrentStep(5)
                  }}
                  onBackClick={() => setCurrentStep(3)}
                />
              )}
              {currentStep === 5 && (
                <InstallationAddressStep
                  data={installationFormData}
                  onNextClick={payload => {
                    setInstallationFormData({
                      ...installationFormData,
                      ...payload
                    })
                    setCurrentStep(6)
                  }}
                  partnershipAddress={partnershipAddress}
                  onBackClick={() => setCurrentStep(4)}
                />
              )}
              {currentStep === 6 && (
                <InstallationReviewStep
                  data={installationFormData}
                  isPending={isPendingCreatePartnershipWithInstallation}
                  onNextClick={() => onCreate()}
                  onBackClick={() => setCurrentStep(5)}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </GenericFullscreenDialog>
  )
}
